import { APPLY_TITLE, APPLY_SUBTITLE } from '@/config/brandConfig'

/**
 * Apply Page Configuration
 *
 * All text, labels, and settings for the Apply page.
 * Makes the form easily configurable without changing component code.
 *
 * To add a new field:
 * 1. Add it to the fields object below
 * 2. Update ApplyFormData interface
 * 3. Add the field to ApplyPage.tsx form
 *
 * FUTURE: Could be loaded from database/CMS for dynamic updates
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

export const applyPageConfig = {
  // Page header
  title: APPLY_TITLE,
  subtitle: APPLY_SUBTITLE,

  // Form fields - easily add/modify fields here
  fields: {
    firstName: {
      label: 'First Name',
      placeholder: 'Enter your first name',
      required: true,
      type: 'text' as const,
      minLength: 1
    },
    lastName: {
      label: 'Last Name',
      placeholder: 'Enter your last name',
      required: true,
      type: 'text' as const,
      minLength: 1
    },
    // Legacy field - kept for backwards compatibility
    name: {
      label: 'Your Name',
      placeholder: 'Enter your full name',
      required: true,
      type: 'text' as const,
      minLength: 2
    },
    email: {
      label: 'Email Address',
      placeholder: 'you@example.com',
      required: true,
      type: 'email' as const
    },
    phone: {
      label: 'Phone Number',
      placeholder: '+1 (555) 123-4567',
      required: true,
      type: 'tel' as const,
      helpText: 'We will send a verification code via SMS'
    },
    reason: {
      label: 'Why do you want to join?',
      placeholder: 'Tell me a bit about yourself and why you want to join...',
      required: true,
      type: 'textarea' as const,
      minLength: 10,
      maxLength: 1000,
      rows: 4
    }
  },

  // Phone verification messages
  phoneVerification: {
    sendCodeButton: 'Send Code',
    verifyButton: 'Verify',
    retryButton: 'Retry',
    skipButton: 'Skip',
    verifiedText: 'Verified',
    resendText: 'Resend Code',
    errorInternational: 'SMS delivery failed. This may be due to international restrictions.',
    errorGeneric: 'Failed to send verification code. Please check your number and try again.'
  },

  // Submit button
  submitButton: {
    label: 'Submit Application',
    loadingLabel: 'Submitting...'
  },

  // Back button
  backButton: {
    label: '\u2190 Back to Login'  // ← arrow
  },

  // Success state
  successMessage: {
    title: 'Application Received!',
    body: "Thank you for applying! I'll review your application and get back to you soon.",
    icon: '\uD83D\uDC8C',  // 💌
    buttonLabel: 'Back to Login'
  },

  // Error messages
  errors: {
    network: 'Network error. Please check your connection and try again.',
    generic: 'Failed to submit application. Please try again.'
  },

  // Feature flags for future expansion
  features: {
    showSocialLinks: false,
    requirePhoneNumber: true,  // Now required
    enableFileUpload: false,
    customFieldsEnabled: false
  }
}

// ============================================================================
// TYPES
// ============================================================================

/**
 * Form data structure - matches the fields configuration
 */
export interface ApplyFormData {
  firstName: string
  lastName: string
  name: string  // Generated from firstName + lastName
  email: string
  phoneNumber: string
  phoneVerified: boolean
  phoneVerificationSkipped: boolean
  reason: string
}

/**
 * Field configuration type
 */
export interface FieldConfig {
  label: string
  placeholder: string
  required: boolean
  type: 'text' | 'email' | 'textarea' | 'tel'
  minLength?: number
  maxLength?: number
  rows?: number
}
